import { Alert, FormControlLabel, Grid, LinearProgress, Stack, Switch } from "@mui/material";
import type { FC } from "react";
import { useEffect, useState } from "react";
import "react-calendar/dist/Calendar.css";
import AsyncLoadingButton from "../../../components/asyncLoadingButton/AsyncLoadingButton";
import type { AdminProductOptionResponse } from "../productsApi";
import { useProductOptions } from "../useProductOptions";
import LinkOptionPrices from "./LinkOptionPrices";
import PriceListFromOtherOption from "./PriceListFromOtherOptionAlert";
import PriceModeSelector, { PriceMode } from "./PriceModeSelector";
import "./ProductOptionEdit.scss";
import UnitInput from "./UnitInput";
import UnlinkOptionPrices from "./UnlinkOptionPrices";
import type { OptionPrices, PriceListPriceValue } from "./updateOptionPrices";
import { updateOptionPrices } from "./updateOptionPrices";
import { useOptionPrices } from "./useOptionPrices";
import OptionNameSelect from "./OptionNameSelect";
import OptionPricesCalendar from "./OptionPricesCalendar";
import dayjs from "../../../dayjs";
import { Controller, useForm, useWatch } from "react-hook-form";
import FormNumberField from "../../../components/formComponents/FormNumberField";
import FormTextField from "../../../components/formComponents/FormTextField";
import FormInfoEdit from "../../../components/formComponents/FormInfoEdit";
import FormSwitch from "../../../components/formComponents/FormSwitch";

interface DetailsEditProps {
  readonly existingOption: AdminProductOptionResponse;
  readonly useDynamicPrices: boolean;
  readonly computePricesFromPurchasePrice: boolean;
  readonly onSubmit: (option: AdminProductOptionResponse) => Promise<void>;
  readonly onDelete: (optionId: string) => Promise<void>;
}

const DetailsEdit: FC<DetailsEditProps> = ({
  existingOption,
  useDynamicPrices,
  computePricesFromPurchasePrice,
  onSubmit,
  onDelete,
}) => {
  const { control, handleSubmit, reset } = useForm<AdminProductOptionResponse>({
    defaultValues: existingOption,
    mode: "onBlur",
  });
  const priceMargin = useWatch({ control, name: "priceMargin" });

  useEffect(() => {
    reset(existingOption);
  }, [existingOption]);

  return (
    <Grid container size={{ xs: 12, lg: 4 }} spacing={2}>
      <Grid
        size={12}
        container
        spacing={2}
        sx={{
          alignContent: "flex-start",
        }}
      >
        <Grid size={12}>
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <OptionNameSelect value={value} label="Nazwa opcji" onChange={onChange} />
            )}
          />
        </Grid>
        <Grid size={12}>
          <Controller
            name="unit"
            control={control}
            render={({ field: { onChange, value } }) => <UnitInput value={value} onChange={onChange} />}
          />
        </Grid>
        <Grid size={12}>
          <FormTextField label="Nazwa dla dostawcy" name="providerDetails.optionName" control={control} />
        </Grid>
        {useDynamicPrices && (
          <Grid size={8}>
            <FormTextField label="ID u dostawcy" name="providerDetails.optionId" control={control} />
          </Grid>
        )}
        {useDynamicPrices && (
          <Grid size={4}>
            <FormNumberField
              label="Przesunięcie daty"
              name="providerDetails.dateMatchShift"
              control={control}
              min={0}
              max={1}
              defaultValue={0}
            />
          </Grid>
        )}
        <Grid size={12}>
          <FormInfoEdit label="Info" name="info.polish" control={control} minRows={2} />
        </Grid>
        <Grid size={12}>
          <FormInfoEdit label="Info (eng)" name="info.english" control={control} minRows={2} />
        </Grid>
        <Grid size={12}>
          <FormSwitch label="Opcja wewnętrzna" name="internal" control={control} />
        </Grid>
        {computePricesFromPurchasePrice && (
          <Grid size={12}>
            <Controller
              name="priceMargin"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value != null}
                      onChange={(e) => onChange(e.target.checked ? { multiplier: 1, amount: 0 } : null)}
                    />
                  }
                  label="Niestandardowa marża"
                />
              )}
            />
          </Grid>
        )}
        {computePricesFromPurchasePrice && priceMargin != null && (
          <>
            <Grid size={6}>
              <FormNumberField label="Mnożnik marży" name="priceMargin.multiplier" control={control} />
            </Grid>
            <Grid size={6}>
              <FormNumberField label="Kwota marży" name="priceMargin.amount" control={control} />
            </Grid>
          </>
        )}
      </Grid>
      <Grid
        container
        size={12}
        sx={{
          alignContent: "flex-end",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <AsyncLoadingButton onClick={() => onDelete(existingOption.id)} color="error">
          Usuń opcję
        </AsyncLoadingButton>
        <AsyncLoadingButton onClick={handleSubmit(onSubmit)}>Zapisz</AsyncLoadingButton>
      </Grid>
    </Grid>
  );
};

const MAX_BEFORE_DYNAMIC_PRICES_DATE = dayjs("2025-12-31").endOf("year");

interface PricesEditProps {
  readonly productId: string;
  readonly providerId: string;
  readonly useDynamicPrices: boolean;
  readonly computePricesFromPurchasePrice: boolean;
  readonly optionName: string;
  readonly isLoading: boolean;
  readonly currentPrices?: OptionPrices;
  readonly usePricesFrom?: { productId: string; optionId: string };
  readonly onSubmit: (prices: OptionPrices) => Promise<void>;
  readonly onLinkOptionPrices: (value: { productId: string; optionId: string }) => Promise<void>;
  readonly onRemoveOptionPricesLink: (copyPrices: boolean) => Promise<void>;
}

const PricesEdit: FC<PricesEditProps> = ({
  productId,
  providerId,
  useDynamicPrices,
  computePricesFromPurchasePrice,
  optionName,
  usePricesFrom,
  currentPrices,
  isLoading,
  onSubmit,
  onLinkOptionPrices,
  onRemoveOptionPricesLink,
}) => {
  const [priceMode, setPriceMode] = useState(PriceMode.SELLING_PRICE);
  const [prices, setPrices] = useState<OptionPrices | undefined>(currentPrices);

  useEffect(() => {
    setPrices(currentPrices);
  }, [currentPrices]);

  const handleDaysValueChange = (days: string[], value: PriceListPriceValue) => {
    setPrices((p) => (p ? updateOptionPrices(p, priceMode, days, value) : p));
  };

  const handleSavePriceList = async () => {
    if (prices) {
      await onSubmit(prices);
    }
  };

  return (
    <Grid size={{ xs: 12, lg: 8 }} container spacing={2}>
      <Grid size={12}>
        <PriceModeSelector value={priceMode} onChange={(v) => setPriceMode(v)} />
      </Grid>
      {isLoading && (
        <Grid size={12}>
          <LinearProgress />
        </Grid>
      )}
      {prices && usePricesFrom && (
        <>
          <Grid size={12}>
            <PriceListFromOtherOption productId={usePricesFrom.productId} optionId={usePricesFrom.optionId} />
          </Grid>
          <Grid
            container
            size={12}
            sx={{
              alignContent: "flex-end",
              justifyContent: "flex-end",
            }}
          >
            <Stack direction="row">
              <UnlinkOptionPrices onUnlink={onRemoveOptionPricesLink} />
            </Stack>
          </Grid>
        </>
      )}
      {prices && !usePricesFrom && (
        <>
          {priceMode === PriceMode.SELLING_PRICE && computePricesFromPurchasePrice && (
            <Grid size={12}>
              <Alert severity="info">
                Ceny sprzedaży są przeliczane automatycznie z cen zakupu i marży — nie można ich edytować ręcznie.
              </Alert>
            </Grid>
          )}
          <Grid size={12} data-testid="price-list-month">
            <OptionPricesCalendar
              priceMode={priceMode}
              prices={prices}
              maxDate={useDynamicPrices ? MAX_BEFORE_DYNAMIC_PRICES_DATE : undefined}
              sellingPricesReadOnly={computePricesFromPurchasePrice}
              onDaysValueChange={handleDaysValueChange}
            />
          </Grid>
          <Grid
            container
            size={12}
            spacing={2}
            sx={{
              alignContent: "flex-end",
              justifyContent: "flex-end",
            }}
          >
            <Stack spacing={2} direction="row">
              {!usePricesFrom && (
                <LinkOptionPrices
                  productId={productId}
                  providerId={providerId}
                  optionName={optionName}
                  onSelectOption={onLinkOptionPrices}
                />
              )}
              <AsyncLoadingButton onClick={handleSavePriceList}>Zapisz cennik</AsyncLoadingButton>
            </Stack>
          </Grid>
        </>
      )}
    </Grid>
  );
};

interface Props {
  readonly productId: string;
  readonly provider: {
    readonly id: string;
    readonly useDynamicPrices: boolean;
    readonly computePricesFromPurchasePrice: boolean;
  };
  readonly option: AdminProductOptionResponse;
}

const ProductOptionEdit: FC<Props> = ({ productId, provider, option }) => {
  const { updateOption, deleteOption } = useProductOptions(productId);
  const { data, isLoading, updatePrices, linkOptionPrices, unlinkOptionPrices } = useOptionPrices(productId, option.id);

  if (!option) {
    return null;
  }

  return (
    <Grid container spacing={2} className="product-option-edit" data-testid="product-option-edit">
      <DetailsEdit
        existingOption={option}
        useDynamicPrices={provider.useDynamicPrices}
        computePricesFromPurchasePrice={provider.computePricesFromPurchasePrice}
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onSubmit={async ({ id, participantListRequired, multiplier, ...option }) => {
          await updateOption(id, option);
        }}
        onDelete={deleteOption}
      />
      <PricesEdit
        productId={productId}
        providerId={provider.id}
        useDynamicPrices={provider.useDynamicPrices}
        computePricesFromPurchasePrice={data?.computePricesFromPurchasePrice ?? false}
        optionName={option.name}
        currentPrices={data?.prices}
        usePricesFrom={data?.usePricesFrom}
        isLoading={isLoading}
        onSubmit={async (r) => {
          await updatePrices(r);
        }}
        onLinkOptionPrices={async (v) => {
          await linkOptionPrices(productId, option.id, v);
        }}
        onRemoveOptionPricesLink={(copyPrices) => unlinkOptionPrices(productId, option.id, copyPrices)}
      />
    </Grid>
  );
};

export default ProductOptionEdit;
